import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateTime, formatDay } from "@/lib/format";
import { PageHeader } from "@/components/page-header";
import { StateBadge } from "@/components/state-badge";
import { EvalReportView } from "@/components/eval-report";
import { ReasonDialog } from "@/components/reason-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState, Loading } from "@/components/query-state";
import type { PolicyState } from "@/lib/types";
import { useTranslation } from "react-i18next";

type Action = "rollback" | "suspend" | "resume";
const ACTION_TITLE_KEY: Record<Action, string> = {
  rollback: "detail.rollbackTitle",
  suspend: "detail.suspendTitle",
  resume: "detail.resumeTitle",
};
const POLICY_STATES = new Set<PolicyState>(["draft", "evaluating", "scheduled", "live", "superseded", "rolled_back", "expired", "suspended"]);

export function PolicyDetailPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const feedback = useLocation().state as { publishMessage?: string } | null;
  const client = useQueryClient();
  const policy = useQuery({ queryKey: ["policy", id], queryFn: () => api.getPolicy(id) });
  const versions = useQuery({ queryKey: ["policy", id, "versions"], queryFn: () => api.versions(id) });
  const evals = useQuery({ queryKey: ["policy", id, "evals"], queryFn: () => api.evals(id) });

  const [action, setAction] = useState<Action | null>(null);
  const [comparing, setComparing] = useState(false);
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);

  const run = useMutation({
    mutationFn: ({ kind, reason }: { kind: Action; reason: string }) => api[kind](id, reason),
    onSuccess: () => { setAction(null); void client.invalidateQueries({ queryKey: ["policy", id] }); },
  });

  const list = versions.data ?? [];
  const diffFrom = from ?? list[1]?.version ?? null;
  const diffTo = to ?? list[0]?.version ?? null;
  const diff = useQuery({
    queryKey: ["policy", id, "diff", diffFrom, diffTo],
    queryFn: () => api.diff(id, diffFrom!, diffTo!),
    enabled: comparing && diffFrom !== null && diffTo !== null,
  });

  if (policy.isPending) return <Loading />;
  if (policy.isError) return <ErrorState error={policy.error} />;
  const p = policy.data;
  const live = p.live ?? list.find((v) => v.version === p.liveVersion) ?? null;
  const title = live?.content.title ?? p.title ?? p.policyId;

  return (
    <>
      <PageHeader
        title={title}
        description={t("detail.description", { id: p.policyId, domain: p.domainKey })}
        sacredIcon="seal"
        sacredCaption="holy.detailCaption"
        actions={
          <>
            <StateBadge state={p.state} />
            <Link to={`/policies/${id}/edit`}><Button variant="secondary">{t("detail.edit")}</Button></Link>
            {p.state === "live" ? <Button variant="danger" onClick={() => setAction("rollback")}>{t("detail.rollback")}</Button> : null}
            {p.state === "live" ? <Button variant="secondary" onClick={() => setAction("suspend")}>{t("detail.suspend")}</Button> : null}
            {p.state === "suspended" ? <Button onClick={() => setAction("resume")}>{t("detail.resume")}</Button> : null}
          </>
        }
      />
      {feedback?.publishMessage ? <p role="status" className="text-secondary mb-4 text-sm">{feedback.publishMessage}</p> : null}
      {run.isError ? <div className="mb-4"><ErrorState error={run.error} /></div> : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader><CardTitle sacredIcon="seal">{t("detail.liveVersion")}</CardTitle>{live ? <Badge tone="accent">v{live.version}</Badge> : null}</CardHeader>
            <CardBody>
              {live ? (
                <dl className="flex flex-col gap-4 text-sm">
                  <div><dt className="text-muted text-xs">{t("detail.situation")}</dt><dd className="text-foreground mt-0.5">{live.content.situation}</dd></div>
                  <div><dt className="text-muted text-xs">{t("detail.rule")}</dt><dd className="text-foreground mt-0.5">{live.content.rule}</dd></div>
                  <div><dt className="text-muted text-xs">{t("detail.customerText")}</dt><dd className="text-foreground mt-0.5">{live.content.customer_text}</dd></div>
                  <div>
                    <dt className="text-muted text-xs">{t("detail.effective")}</dt>
                    <dd className="text-foreground mt-0.5">
                      {formatDay(live.content.effective_from, i18n.resolvedLanguage)}
                      {live.content.effective_until ? t("detail.until", { date: formatDay(live.content.effective_until, i18n.resolvedLanguage) }) : t("detail.noEndDate")}
                    </dd>
                  </div>
                </dl>
              ) : <p className="text-muted text-sm">{t("detail.noLiveVersion")}</p>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle sacredIcon="tablets">{t("detail.compare")}</CardTitle>
              <Button variant="secondary" size="sm" disabled={list.length < 2} onClick={() => setComparing(true)}>{t("detail.compare")}</Button>
            </CardHeader>
            {comparing ? (
              <CardBody>
                <div className="mb-3 flex items-center gap-2 text-sm">
                  <label htmlFor="diff-from" className="text-muted">{t("detail.from")}</label>
                  <select id="diff-from" className="border-border h-8 rounded-md border px-2" value={diffFrom ?? ""} onChange={(e) => setFrom(Number(e.target.value))}>
                    {list.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
                  </select>
                  <label htmlFor="diff-to" className="text-muted">{t("detail.to")}</label>
                  <select id="diff-to" className="border-border h-8 rounded-md border px-2" value={diffTo ?? ""} onChange={(e) => setTo(Number(e.target.value))}>
                    {list.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
                  </select>
                </div>
                {diff.isPending ? <Loading /> : diff.isError ? <ErrorState error={diff.error} /> : diff.data.changes.length === 0 ? (
                  <p className="text-muted text-sm">{t("detail.noDifferences")}</p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {diff.data.changes.map((c) => (
                      <li key={c.field} className="border-border-subtle rounded-lg border p-3 text-sm">
                        <p className="text-foreground font-medium">{c.field}</p>
                        <p className="bg-danger-soft mt-1 rounded px-2 py-1 line-through">{String(JSON.stringify(c.from) ?? "").replace(/^"|"$/g, "")}</p>
                        <p className="bg-success-soft mt-1 rounded px-2 py-1">{String(JSON.stringify(c.to) ?? "").replace(/^"|"$/g, "")}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            ) : null}
          </Card>

          <Card>
            <CardHeader><CardTitle sacredIcon="scale">{t("detail.evaluations")}</CardTitle></CardHeader>
            <CardBody className="flex flex-col gap-5">
              {evals.isPending ? <Loading /> : evals.isError ? <ErrorState error={evals.error} /> : evals.data.length === 0 ? (
                <p className="text-muted text-sm">{t("detail.noEvaluations")}</p>
              ) : evals.data.map((e) => (
                <div key={e.id}>
                  <p className="text-muted mb-2 text-xs">v{e.version} · {formatDateTime(e.at, i18n.resolvedLanguage)}</p>
                  <EvalReportView report={e.report} label={t("detail.evaluationVersion", { version: e.version })} />
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        <Card className="h-fit">
          <CardHeader><CardTitle sacredIcon="scroll">{t("detail.versions")}</CardTitle></CardHeader>
          <CardBody>
            {versions.isPending ? <Loading /> : versions.isError ? <ErrorState error={versions.error} /> : (
              <ol aria-label={t("detail.versionsList")} className="border-border-subtle relative ml-2 flex flex-col gap-4 border-l pl-4">
                {list.map((v) => (
                  <li key={v.version} className="text-sm">
                    <div className="flex items-center gap-2"><span className="text-foreground font-medium">v{v.version}</span>{POLICY_STATES.has(v.status as PolicyState) ? <StateBadge state={v.status as PolicyState} /> : <Badge tone="neutral">{v.status}</Badge>}</div>
                    <p className="text-muted text-xs">{formatDateTime(v.createdAt, i18n.resolvedLanguage)}</p>
                    <p className="text-muted text-xs">{t("detail.versionAuthor")}: <span className="text-secondary">{v.createdBy}</span></p>
                    <p className="text-secondary mt-0.5 text-xs">{v.content.change_reason}</p>
                  </li>
                ))}
              </ol>
            )}
          </CardBody>
        </Card>
      </div>

      {action ? (
        <ReasonDialog
          open
          title={t(ACTION_TITLE_KEY[action])}
          description={t("detail.reasonDescription")}
          pending={run.isPending}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => run.mutate({ kind: action, reason })}
        />
      ) : null}
    </>
  );
}
